import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Search, Plus, Minus, ShoppingCart, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';

export default function PedidoFormDashboard() {
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [showClienteList, setShowClienteList] = useState(false);

  const [produtos, setProdutos] = useState([]);
  const [prodSearch, setProdSearch] = useState('');
  const [cart, setCart] = useState({});
  const [prazo, setPrazo] = useState('48h');
  const [prazosDisponiveis, setPrazosDisponiveis] = useState(['48h', '72h']);
  const [observacoes, setObservacoes] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Busca clientes ativos
  const buscarClientes = useCallback(async (q) => {
    if (!q || q.length < 2) { setClientes([]); return; }
    try {
      const res = await api.get(`/clientes?status=Ativo&search=${encodeURIComponent(q)}`);
      setClientes(res.data?.clientes || res.data || []);
    } catch { setClientes([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscarClientes(clienteSearch), 300);
    return () => clearTimeout(t);
  }, [clienteSearch, buscarClientes]);

  // Ao selecionar cliente, carrega produtos com preços do tipo de cliente
  const selecionarCliente = async (c) => {
    setClienteSelecionado(c);
    setClienteSearch(c.nome_empresa || c.nome_contato);
    setShowClienteList(false);
    setCart({});
    setLoading(true);
    setError('');
    try {
      // Busca produtos. Para obter os preços do tipo do cliente, precisamos logar como ele não é possível.
      // Em vez disso, buscamos os preços pela tabela do tipo_cliente_id do cliente.
      const [prodRes, precosMap] = await Promise.all([
        api.get('/produtos?is_ativo=true'),
        api.get(`/clientes/${c.id}/precos`).catch(() => ({ data: {} })),
      ]);
      const prods = prodRes.data.map(p => ({
        ...p,
        preco_unitario: precosMap.data?.[p.id] ?? null,
      }));
      setProdutos(prods);
      // Configurar prazos baseado no cliente
      const opcoes = c.prazo_24h ? ['24h', '48h', '72h'] : ['48h', '72h'];
      setPrazosDisponiveis(opcoes);
      setPrazo(opcoes[0]);
    } catch {
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const setQty = (pid, qty) => {
    if (qty <= 0) {
      setCart(c => { const n = { ...c }; delete n[pid]; return n; });
    } else {
      setCart(c => ({ ...c, [pid]: qty }));
    }
  };

  const cartItems = Object.entries(cart).map(([pid, qty]) => {
    const p = produtos.find(x => x.id === pid);
    return p ? { ...p, qty } : null;
  }).filter(Boolean);

  const cartTotal = cartItems.reduce((acc, i) => acc + (i.preco_unitario || 0) * i.qty, 0);

  const prodsFiltrados = produtos.filter(p =>
    !prodSearch || p.descricao?.toLowerCase().includes(prodSearch.toLowerCase()) || p.sku?.toLowerCase().includes(prodSearch.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!clienteSelecionado) { setError('Selecione um cliente'); return; }
    if (cartItems.length === 0) { setError('Adicione pelo menos 1 produto'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/pedidos', {
        cliente_id: clienteSelecionado.id,
        itens: cartItems.map(i => ({ produto_id: i.id, quantidade: i.qty })),
        prazo,
        observacoes,
      });
      navigate(`/admin/pedidos/${res.data.id}`);
    } catch (err) {
      setError(err.userMessage || err.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="pedido-form-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/pedidos')} className="p-2 rounded-lg hover:bg-brand-bg">
          <ArrowLeft className="w-5 h-5 text-brand-text-muted" />
        </button>
        <div>
          <h1 className="dashboard-heading text-2xl">Novo Pedido</h1>
          <p className="text-brand-text-muted font-body text-sm">Criando pedido pelo dashboard</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 font-body" data-testid="pedido-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Coluna esquerda: cliente + produtos */}
        <div className="lg:col-span-2 space-y-4">

          {/* Seleção de cliente */}
          <div className="card-dashboard">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3 text-sm">Cliente</h3>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-brand-text-muted" />
              <input
                type="text"
                placeholder="Buscar cliente pelo nome ou empresa..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-brand-border text-sm font-body"
                value={clienteSearch}
                onChange={e => { setClienteSearch(e.target.value); setShowClienteList(true); }}
                onFocus={() => setShowClienteList(true)}
                data-testid="cliente-search"
              />
              {showClienteList && clientes.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border border-brand-border rounded-xl shadow-xl max-h-56 overflow-y-auto">
                  {clientes.map(c => (
                    <button
                      key={c.id}
                      className="w-full text-left px-4 py-2.5 hover:bg-brand-bg text-sm font-body border-b border-brand-border/50 last:border-0"
                      onClick={() => selecionarCliente(c)}
                      data-testid="cliente-option"
                    >
                      <span className="font-semibold text-brand-text">{c.nome_empresa || c.nome_contato}</span>
                      {c.nome_empresa && <span className="text-brand-text-muted ml-2">· {c.nome_contato}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {clienteSelecionado && (
              <div className="mt-3 flex items-center gap-2 text-xs font-body text-brand-text-muted bg-brand-bg rounded-lg px-3 py-2">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                Cliente selecionado: <span className="font-semibold text-brand-text ml-1">{clienteSelecionado.nome_empresa || clienteSelecionado.nome_contato}</span>
                {clienteSelecionado.turno && <span className="ml-auto">Turno: {clienteSelecionado.turno}</span>}
              </div>
            )}
          </div>

          {/* Produtos */}
          {clienteSelecionado && (
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-dashboard font-semibold text-brand-text text-sm">Produtos</h3>
                {loading && <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />}
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-brand-text-muted" />
                <input
                  type="text"
                  placeholder="Filtrar produtos..."
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-brand-border text-sm font-body"
                  value={prodSearch}
                  onChange={e => setProdSearch(e.target.value)}
                />
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {prodsFiltrados.length === 0 && !loading && (
                  <p className="text-center text-brand-text-muted text-sm py-6 font-body">Nenhum produto encontrado</p>
                )}
                {prodsFiltrados.map(p => (
                  <div key={p.id} className="flex items-center gap-3 py-2 border-b border-brand-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-body font-medium text-brand-text truncate">{p.descricao}</p>
                      <p className="text-xs text-brand-text-muted font-body">{p.sku} · {p.unidade_medida || 'Un'}{p.pedido_minimo > 1 ? ` · mín. ${p.pedido_minimo}` : ''}</p>
                    </div>
                    <span className="text-sm font-dashboard font-semibold text-brand-primary w-20 text-right flex-shrink-0">
                      {p.preco_unitario != null ? formatCurrency(p.preco_unitario) : <span className="text-brand-text-muted text-xs">sem preço</span>}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setQty(p.id, (cart[p.id] || 0) - 1)} className="w-7 h-7 flex items-center justify-center rounded-lg border border-brand-border hover:bg-brand-bg text-brand-text-muted">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-sm font-dashboard font-semibold">{cart[p.id] || 0}</span>
                      <button onClick={() => setQty(p.id, (cart[p.id] || 0) + 1)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-primary text-white hover:bg-brand-dark">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Coluna direita: resumo do pedido */}
        <div className="space-y-4">
          <div className="card-dashboard">
            <h3 className="font-dashboard font-semibold text-brand-text mb-4 text-sm">Resumo do Pedido</h3>

            {/* Prazo */}
            <div className="mb-4">
              <p className="text-xs text-brand-text-muted font-body mb-2">Prazo de entrega</p>
              <div className="flex gap-2">
                {prazosDisponiveis.map(p => (
                  <button
                    key={p}
                    onClick={() => setPrazo(p)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-body font-medium border-2 transition-colors ${prazo === p ? 'bg-brand-primary text-white border-brand-primary' : 'border-brand-border text-brand-text-muted'}`}
                    data-testid={`prazo-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Itens do carrinho */}
            {cartItems.length > 0 ? (
              <div className="space-y-2 mb-4">
                {cartItems.map(i => (
                  <div key={i.id} className="flex items-center justify-between text-xs font-body">
                    <span className="text-brand-text flex-1 truncate">{i.descricao}</span>
                    <span className="text-brand-text-muted mx-2 flex-shrink-0">×{i.qty}</span>
                    <span className="font-semibold text-brand-text flex-shrink-0">
                      {i.preco_unitario != null ? formatCurrency(i.preco_unitario * i.qty) : '–'}
                    </span>
                    <button onClick={() => setQty(i.id, 0)} className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="border-t border-brand-border pt-2 flex justify-between font-dashboard font-semibold text-sm">
                  <span>Total</span>
                  <span className="text-brand-primary">{cartTotal > 0 ? formatCurrency(cartTotal) : 'A calcular'}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-brand-text-muted text-xs font-body mb-4">
                <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Nenhum produto adicionado
              </div>
            )}

            {/* Observações */}
            <div className="mb-4">
              <p className="text-xs text-brand-text-muted font-body mb-1.5">Observações</p>
              <textarea
                rows={2}
                placeholder="Observações do pedido (opcional)..."
                className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body resize-none"
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                data-testid="observacoes"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !clienteSelecionado || cartItems.length === 0}
              className="w-full py-3 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-40"
              data-testid="submit-pedido-btn"
            >
              {submitting ? 'Salvando...' : 'Confirmar Pedido'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
